import { MapPolygon } from '@/components/MapView';

export interface GeoJSONFeature {
  type: string;
  geometry: {
    type: string;
    coordinates: any;
  };
  properties?: any;
  id?: string;
}

export interface GeoJSONFeatureCollection {
  type: string;
  features: GeoJSONFeature[];
}

export interface PolygonExtractionOptions {
  idPrefix?: string;
  strokeColor?: string;
  fillColor?: string;
  strokeWidth?: number;
  onPress?: (feature: GeoJSONFeature, polygonIndex?: number) => void;
  propertyExtractor?: (feature: GeoJSONFeature, index?: number) => string;
  styleExtractor?: (feature: GeoJSONFeature, index?: number) => {
    strokeColor?: string;
    fillColor?: string;
    strokeWidth?: number;
  };
}

/**
 * Converts GeoJSON coordinates to MapPolygon coordinates format
 * @param coordinates - GeoJSON coordinate array
 * @returns Array of {latitude, longitude} objects
 */
export const convertGeoJSONCoordinates = (coordinates: number[][]): { latitude: number; longitude: number }[] => {
  return coordinates.map((coord: number[]) => {
    const [lng, lat] = coord;
    return { latitude: lat || 0, longitude: lng || 0 };
  });
};

/**
 * Extracts polygons from a GeoJSON feature
 * @param feature - GeoJSON feature
 * @param options - Configuration options for polygon extraction
 * @returns Array of MapPolygon objects
 */
export const extractPolygonsFromFeature = (
  feature: GeoJSONFeature,
  options: PolygonExtractionOptions = {}
): MapPolygon[] => {
  const {
    idPrefix = 'polygon',
    strokeColor = '#007AFF',
    fillColor = 'rgba(0,122,255,0.2)',
    strokeWidth = 2,
    onPress,
    propertyExtractor,
    styleExtractor
  } = options;

  const result: MapPolygon[] = [];
  
  // Extract identifier from feature properties or use feature ID
  const identifier = propertyExtractor ? propertyExtractor(feature) : (feature.id || 'unknown');
  
  // Get dynamic styling if provided
  const dynamicStyle = styleExtractor ? styleExtractor(feature) : {};
  const finalStrokeColor = dynamicStyle.strokeColor || strokeColor;
  const finalFillColor = dynamicStyle.fillColor || fillColor;
  const finalStrokeWidth = dynamicStyle.strokeWidth || strokeWidth;
  
  if (feature.geometry?.type === 'Polygon') {
    // Handle Polygon geometry
    if (feature.geometry.coordinates && feature.geometry.coordinates[0]) {
      const coordinates = convertGeoJSONCoordinates(feature.geometry.coordinates[0]);
      
      const polygon: MapPolygon = {
        id: `${idPrefix}-${identifier}`,
        coordinates,
        strokeColor: finalStrokeColor,
        fillColor: finalFillColor,
        strokeWidth: finalStrokeWidth,
      };
      
      if (onPress) {
        polygon.onPress = () => onPress(feature);
      }
      
      result.push(polygon);
    }
  } else if (feature.geometry?.type === 'MultiPolygon') {
    // Handle MultiPolygon geometry - create separate polygons for each part
    feature.geometry.coordinates.forEach((polygonCoords: number[][][], polygonIndex: number) => {
      if (polygonCoords && polygonCoords[0]) {
        const coordinates = convertGeoJSONCoordinates(polygonCoords[0]);
        
        const polygon: MapPolygon = {
          id: `${idPrefix}-${identifier}-${polygonIndex}`,
          coordinates,
          strokeColor: finalStrokeColor,
          fillColor: finalFillColor,
          strokeWidth: finalStrokeWidth,
        };
        
        if (onPress) {
          polygon.onPress = () => onPress(feature, polygonIndex);
        }
        
        result.push(polygon);
      }
    });
  }
  
  return result;
};

/**
 * Extracts all polygons from a GeoJSON FeatureCollection
 * @param featureCollection - GeoJSON FeatureCollection
 * @param options - Configuration options for polygon extraction
 * @returns Array of MapPolygon objects
 */
export const extractPolygonsFromFeatureCollection = (
  featureCollection: GeoJSONFeatureCollection,
  options: PolygonExtractionOptions = {}
): MapPolygon[] => {
  if (!featureCollection || !featureCollection.features) {
    return [];
  }
  
  const result: MapPolygon[] = [];
  
  featureCollection.features.forEach((feature: GeoJSONFeature, featureIndex: number) => {
    // Add feature index to options for unique ID generation
    const featureOptions = {
      ...options,
      propertyExtractor: options.propertyExtractor || ((feature: GeoJSONFeature, index?: number) => 
        feature.properties?.postcode || feature.id || `feature-${index || featureIndex}`
      )
    };
    
    const polygons = extractPolygonsFromFeature(feature, featureOptions);
    result.push(...polygons);
  });
  
  return result;
};

/**
 * Calculates the bounding box for a GeoJSON feature
 * @param feature - GeoJSON feature
 * @returns Bounding box as {minLat, maxLat, minLng, maxLng}
 */
export const calculateFeatureBoundingBox = (feature: GeoJSONFeature): {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
} => {
  let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
  
  if (feature.geometry?.type === 'Polygon' && feature.geometry.coordinates?.[0]) {
    feature.geometry.coordinates[0].forEach((coord: number[]) => {
      const [lng, lat] = coord;
      if (lng !== undefined && lat !== undefined) {
        minLat = Math.min(minLat, lat);
        maxLat = Math.max(maxLat, lat);
        minLng = Math.min(minLng, lng);
        maxLng = Math.max(maxLng, lng);
      }
    });
  } else if (feature.geometry?.type === 'MultiPolygon' && feature.geometry.coordinates) {
    feature.geometry.coordinates.forEach((polygonCoords: number[][][]) => {
      if (polygonCoords?.[0]) {
        polygonCoords[0].forEach((coord: number[]) => {
          const [lng, lat] = coord;
          if (lng !== undefined && lat !== undefined) {
            minLat = Math.min(minLat, lat);
            maxLat = Math.max(maxLat, lat);
            minLng = Math.min(minLng, lng);
            maxLng = Math.max(maxLng, lng);
          }
        });
      }
    });
  }
  
  return { minLat, maxLat, minLng, maxLng };
};

/**
 * Calculates the bounding box for a GeoJSON FeatureCollection
 * @param featureCollection - GeoJSON FeatureCollection
 * @returns Bounding box as {minLat, maxLat, minLng, maxLng}
 */
export const calculateFeatureCollectionBoundingBox = (featureCollection: GeoJSONFeatureCollection): {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
} => {
  let minLat = 90, maxLat = -90, minLng = 180, maxLng = -180;
  
  if (!featureCollection || !featureCollection.features) {
    return { minLat, maxLat, minLng, maxLng };
  }
  
  featureCollection.features.forEach((feature: GeoJSONFeature) => {
    const bbox = calculateFeatureBoundingBox(feature);
    minLat = Math.min(minLat, bbox.minLat);
    maxLat = Math.max(maxLat, bbox.maxLat);
    minLng = Math.min(minLng, bbox.minLng);
    maxLng = Math.max(maxLng, bbox.maxLng);
  });
  
  return { minLat, maxLat, minLng, maxLng };
};

/**
 * Calculates the center point of a bounding box
 * @param bbox - Bounding box as {minLat, maxLat, minLng, maxLng}
 * @returns Center point as {latitude, longitude}
 */
export const calculateBoundingBoxCenter = (bbox: {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}): { latitude: number; longitude: number } => {
  return {
    latitude: (bbox.minLat + bbox.maxLat) / 2,
    longitude: (bbox.minLng + bbox.maxLng) / 2,
  };
};

/**
 * Calculates the delta values for a bounding box with optional margin
 * @param bbox - Bounding box as {minLat, maxLat, minLng, maxLng}
 * @param margin - Optional margin multiplier (default: 0.1)
 * @returns Delta values as {latitudeDelta, longitudeDelta}
 */
export const calculateBoundingBoxDeltas = (bbox: {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
}, margin: number = 0.1): { latitudeDelta: number; longitudeDelta: number } => {
  const latMargin = (bbox.maxLat - bbox.minLat) * margin;
  const lngMargin = (bbox.maxLng - bbox.minLng) * margin;
  
  return {
    latitudeDelta: (bbox.maxLat - bbox.minLat) + latMargin || 0.05,
    longitudeDelta: (bbox.maxLng - bbox.minLng) + lngMargin || 0.05,
  };
};

/**
 * Calculates the complete map region from a GeoJSON FeatureCollection
 * This combines bounding box calculation, center point, and deltas into one function
 * Handles all fallback cases internally - returns sensible defaults if no valid data
 * @param featureCollection - GeoJSON FeatureCollection (can be null/undefined)
 * @param margin - Optional margin multiplier (default: 0.1)
 * @param fallbackRegion - Optional custom fallback region if no features found
 * @returns Map region as {latitude, longitude, latitudeDelta, longitudeDelta}
 */
export const calculateMapRegionFromFeatureCollection = (
  featureCollection: GeoJSONFeatureCollection | null | undefined,
  margin: number = 0.1,
  fallbackRegion?: {
    latitude: number;
    longitude: number;
    latitudeDelta: number;
    longitudeDelta: number;
  }
): {
  latitude: number;
  longitude: number;
  latitudeDelta: number;
  longitudeDelta: number;
} => {
  // Default fallback region
  const defaultFallback = {
    latitude: 51.505,
    longitude: -0.09,
    latitudeDelta: 0.05,
    longitudeDelta: 0.05,
  };

  // Use custom fallback if provided, otherwise use default
  const finalFallback = fallbackRegion || defaultFallback;

  // Handle all null/undefined cases
  if (!featureCollection || !featureCollection.features || featureCollection.features.length === 0) {
    return finalFallback;
  }

  try {
    const bbox = calculateFeatureCollectionBoundingBox(featureCollection);
    const center = calculateBoundingBoxCenter(bbox);
    const deltas = calculateBoundingBoxDeltas(bbox, margin);
    
    return {
      latitude: center.latitude,
      longitude: center.longitude,
      latitudeDelta: deltas.latitudeDelta,
      longitudeDelta: deltas.longitudeDelta,
    };
  } catch (error) {
    // If any error occurs during calculation, return fallback
    console.warn('Error calculating map region from GeoJSON, using fallback:', error);
    return finalFallback;
  }
};

/**
 * Calculates bounds for a specific postcode from GeoJSON feature collection
 * Leverages existing bounding box functionality for consistency
 * @param postcodeSlicePolygons - GeoJSON FeatureCollection containing postcode polygons
 * @param targetPostcode - The postcode to find bounds for
 * @param margin - Optional margin multiplier (default: 0.2 for 20% padding)
 * @returns Bounds object with center and deltas, or null if postcode not found
 */
export const calculatePostcodeBounds = (
  postcodeSlicePolygons: GeoJSONFeatureCollection | null | undefined,
  targetPostcode: string,
  margin: number = 0.2
): {
  latitude: number;
  longitude: number;
  latitudeDelta: number;
  longitudeDelta: number;
} | null => {
  if (!postcodeSlicePolygons?.features || !targetPostcode) {
    return null;
  }

  // Find the feature(s) for the target postcode
  const postcodeFeatures = postcodeSlicePolygons.features.filter(
    (feature: GeoJSONFeature) => feature.properties?.postcode === targetPostcode
  );

  if (postcodeFeatures.length === 0) {
    return null;
  }

  // Create a temporary feature collection with just the postcode features
  const postcodeFeatureCollection: GeoJSONFeatureCollection = {
    type: 'FeatureCollection',
    features: postcodeFeatures
  };

  try {
    // Use existing bounding box functionality
    const bbox = calculateFeatureCollectionBoundingBox(postcodeFeatureCollection);
    const center = calculateBoundingBoxCenter(bbox);
    const deltas = calculateBoundingBoxDeltas(bbox, margin);

    // Ensure minimum zoom level (not too zoomed out)
    const minDelta = 0.001; // Roughly equivalent to zoom level 15-16
    
    return {
      latitude: center.latitude,
      longitude: center.longitude,
      latitudeDelta: Math.max(deltas.latitudeDelta, minDelta),
      longitudeDelta: Math.max(deltas.longitudeDelta, minDelta),
    };
  } catch (error) {
    console.warn('Error calculating postcode bounds:', error);
    return null;
  }
};
